import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, STATUS_LABEL } from "@/lib/api";
import { PageTitle, Btn, Panel, Pill, statusTone } from "@/admin/ui";

const tomorrow = () => new Date(Date.now() + 19800000 + 86400000).toISOString().slice(0, 10);

export default function AdminPrepList() {
  const [date, setDate] = useState(tomorrow());
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/prep-list", { params: { date } }).then((r) => setD(r.data)); }, [date]);

  return (
    <div className="max-w-5xl">
      <PageTitle sub="Cut items and combos to prepare, totalled across all orders for the day."
        action={<div className="flex gap-2 print:hidden">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40 bg-white" data-testid="prep-date-input" />
          <Btn variant="outline" onClick={() => window.print()} data-testid="prep-print-btn"><Printer className="w-4 h-4 inline mr-1" />Print</Btn>
        </div>}>
        Prep list · {date}
      </PageTitle>
      {!d ? <p className="text-stone-500">Loading…</p> : d.items.length === 0 ? (
        <p className="bg-white border border-stone-200 rounded-md p-6 text-sm text-stone-500" data-testid="prep-empty">No cut items or combos ordered for {d.date}.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="To prepare" testId="prep-items-panel">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-stone-500"><th className="pb-2">Item</th><th className="pb-2 text-right">Packs</th><th className="pb-2 text-right">Orders</th></tr></thead>
                <tbody className="divide-y divide-stone-100">
                  {d.items.map((it, i) => (
                    <tr key={i} data-testid={`prep-item-${i}`}>
                      <td className="py-2"><b>{it.name}</b>{it.cut && <span className="ml-1.5"><Pill tone="orange">{it.cut}</Pill></span>} <span className="text-stone-400">({it.unit})</span></td>
                      <td className="py-2 text-right text-lg font-extrabold text-primary" data-testid={`prep-item-qty-${i}`}>{it.qty}</td>
                      <td className="py-2 text-right text-stone-500">{it.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
            <Panel title="Raw vegetables needed (estimate)" testId="prep-raw-panel">
              {d.raw.length === 0 ? <p className="text-sm text-stone-500">Link combos / cut items to vegetables to see raw quantities.</p> : (
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-stone-100">
                    {d.raw.map((r, i) => (
                      <tr key={i} data-testid={`prep-raw-${i}`}>
                        <td className="py-2">{r.name}</td>
                        <td className="py-2 text-right font-bold">{r.qty} × {r.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-[11px] text-stone-400 mt-2">From combo contents and auto-price links. Plain cut items without a link aren't counted.</p>
            </Panel>
          </div>
          <Panel title={`By order (${d.orders.length})`} testId="prep-orders-panel">
            <div className="divide-y divide-stone-100">
              {d.orders.map((o) => (
                <div key={o.id} className="py-2 flex flex-wrap gap-x-4 gap-y-1 items-baseline text-sm" data-testid={`prep-order-${o.id}`}>
                  <Link to={`/admin/orders/${o.id}`} className="font-bold text-primary w-36">{o.order_number}</Link>
                  <span className="text-stone-500 w-44">{o.slot_label}</span>
                  <span className="w-32 truncate">{o.name}</span>
                  <span className="flex-1">{o.items.map((i) => `${i.name}${i.cut ? ` (${i.cut})` : ""} × ${i.qty}`).join(", ")}</span>
                  <Pill tone={statusTone(o.status)}>{STATUS_LABEL[o.status]}</Pill>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
